import { Component, Input, ViewChild } from '@angular/core';
import { StorageService } from '../../core/storage/storageService';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { InvoiceService } from '../../models/invoices/invoiceService';
import { InvoiceSettings } from '../../models/invoices/invoiceSettings';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';
import { TextModes } from '../../components/ui/controls/text-input/text-input.component';
import { DDLModes } from '../../components/ui/controls/ddl-input/ddl-input.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IconType } from '../../../../../../libs/Shared/components/icon/icon';
import { InvoiceNumberingComponent } from '../invoice-numbering/invoice-numbering.component';

@Component({
  selector: 'app-invoice-settings',
  templateUrl: './invoice-settings.component.html',
  standalone: false,
})
export class InvoiceSettingsComponent {
  invoiceSettings: InvoiceSettings = InvoiceSettings.createEmpty();
  initialInvoiceSettings: InvoiceSettings = InvoiceSettings.createEmpty();

  loadingSpinnerRef: MatDialogRef<LoadingSpinnerComponent> | undefined;

  emailReminderFrequencyOptions: ListItem[] = [
    new ListItem({ id: 'weekly', name: 'Weekly' }),
    new ListItem({ id: 'biweekly', name: 'Biweekly' }),
    new ListItem({ id: 'monthly', name: 'Monthly' }),
    new ListItem({ id: 'custom', name: 'Custom' }),
  ];

  inputTextModes = TextModes;
  IconType = IconType;
  DDLModes = DDLModes;

  hideInitial: boolean = true;
  hideSubsequent: boolean = true;
  hideLate: boolean = true;

  invoiceNumberOptions: ListItem[] = [
    new ListItem({ id: 'auto-generate', name: 'Auto-generate' }),
    new ListItem({ id: 'manual', name: 'Customize per Invoice' }),
  ];
  invoiceNumberMode: string = 'auto-generate';  

  constructor(
    private storageService: StorageService,
    private invoiceService: InvoiceService,
    private uiErrorHandler: UIErrorHandler,
    public loadingSpinnerDialog: MatDialog,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    //See if the merchant has existing invoice settings
    this.invoiceService
      .getInvoiceSettings(this.storageService.getSelectedLocation()!.id)
      .then((invoiceSettings: InvoiceSettings) => {
        //If the merchant id in the invoice settings does not match the selected location id, then the merchant has no invoice settings
        this.invoiceNumberOptions.find(option => option.id === 'auto-generate')!.name = 'Auto-generate (next invoice #' + invoiceSettings.nextNumber + ')'
        if (
          invoiceSettings.merchantId ===
          this.storageService.getSelectedLocation()!.id
        ) {
          this.invoiceSettings = invoiceSettings;
        } else {
          this.invoiceSettings.merchantId =
            this.storageService.getSelectedLocation()!.id;
          this.invoiceService
            .createInvoiceSettings(this.invoiceSettings)
            .then((response: InvoiceSettings) => {
              this.invoiceSettings = response;
            })
            .catch((error: any) => {
              this.uiErrorHandler.handleError(error);
            });
        }
        this.invoiceNumberMode = this.invoiceSettings.generateInvoiceNumber ? 'auto-generate' : 'manual';
        this.initialInvoiceSettings = invoiceSettings;
      })
      .catch((error: any) => {
        console.error(error);
        this.uiErrorHandler.handleError(error);
      });
  }

  onCreateInvoiceClicked() {
    this.invoiceService
      .getInvoiceSettings(this.storageService.getSelectedLocation()?.id || '')
      .then((invoiceSettings: InvoiceSettings) => {
        this.invoiceSettings = invoiceSettings;
        if (!this.invoiceSettings.generateInvoiceNumber) {
          let dialogRef: MatDialogRef<InvoiceNumberingComponent>;
          dialogRef = this.dialog.open(InvoiceNumberingComponent);
          dialogRef.componentInstance.invoiceNumber =
            this.invoiceSettings.nextNumber;
          dialogRef.componentInstance.initialInvoiceNumber =
            this.invoiceSettings.nextNumber;
          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              this.invoiceSettings.nextNumber = result;
            }
            this.createInvoice();
          });
        } else {
          this.createInvoice();
        }
      })
      .catch((error) => {
        console.error('error', error);
        this.uiErrorHandler.handleError(error);
      });
  }

  createInvoice() {
    this.loadingSpinnerRef = this.loadingSpinnerDialog.open(
      LoadingSpinnerComponent,
      {
        disableClose: true,
        panelClass: 'transparent-dialog',
        data: {
          mode: 'indeterminate',
        },
      },
    );
    this.invoiceService
      .createInvoice(
        this.storageService.getSelectedLocation()!.id,
        this.invoiceSettings,
      )
      .then((invoiceId) => {
        this.storageService.setSelectedInvoiceId(invoiceId);
        this.loadingSpinnerRef?.close();
        this.router.navigate(['/merchant/invoices/create']);
      })
      .catch((error) => {
        console.error('error', error);
        this.uiErrorHandler.handleError(error);
      });
  }

  changeInvoiceNumberMode(mode: string) {
    this.invoiceSettings.generateInvoiceNumber = mode === 'auto-generate';
  }

  handleTemplateChanged(description: string, pane: string) {
    switch (pane) {
      case 'initial':
        this.invoiceSettings.initialNotification = description;
        break;
      case 'subsequent':
        this.invoiceSettings.subsequentNotification = description;
        break;
      case 'late':
        this.invoiceSettings.lateReminderNotification = description;
        break;
    }
  }

  handleCancelChanges() {
    this.invoiceSettings = this.initialInvoiceSettings;
    this.router.navigate(['/merchant/invoices']);
  }

  handleSaveChanges() {
    this.loadingSpinnerRef = this.loadingSpinnerDialog.open(
      LoadingSpinnerComponent,
      {
        disableClose: true,
        panelClass: 'transparent-dialog',
        data: {
          mode: 'indeterminate',
        },
      },
    );
    this.invoiceSettings.nextNumber = parseInt(
      this.invoiceSettings.nextNumber.toString(),
    );
    this.invoiceSettings.reminderFrequencyCustomDays = parseInt(
      this.invoiceSettings.reminderFrequencyCustomDays.toString(),
    );
    this.invoiceService
      .putInvoiceSettings(this.invoiceSettings)
      .then((response: boolean) => {
        this.loadingSpinnerRef?.close();
        this.router.navigate(['/merchant/invoices']);
      })
      .catch((error: any) => {
        this.uiErrorHandler.handleError(error);
        this.loadingSpinnerRef?.close();
      });
  }
}
